import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Avatar,
  CardHeader,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowForward as ForwardIcon,
  ArrowBack as BackwardIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Print as PrintIcon,
  History as HistoryIcon,
  PostAdd as PostAddIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  RotateRight as RotateRightIcon,
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import { applicationService } from '../../services/applicationService';
import { workflowService } from '../../services/workflowService';
import { fieldInspectionService } from '../../services/fieldInspectionService';
import LoadingOverlay from '../Shared/LoadingOverlay';
import ExgratiaAmountTable from './ExgratiaAmountTable';
import ReportDetailsDialog from './ReportDetailsDialog';
import ChequeDetails from './ChequeDetails';
import ImageViewer from '../common/ImageViewer';

const DAMAGE_INSPECTION_SECTIONS = [
  {
    section: 'Land & House',
    damageTypes: ['land', 'house'],
    heads: [
      { code: 'reve_o', label: 'Revenue Officer' },
    ],
  },
  {
    section: 'Crop',
    damageTypes: ['crop'],
    heads: [
      { code: 'horti_h', label: 'Horticulture Dept.' },
      { code: 'agri_h', label: 'Agriculture Dept.' },
    ],
  },
  {
    section: 'Livestock',
    damageTypes: ['livestock'],
    heads: [
      { code: 'ahvs_h', label: 'AHVS Dept.' },
    ],
  },
];

const REPORT_STATUS_COLORS = {
  'new': 'info',
  'assigned': 'warning', 
  'in_progress': 'primary',
  'uploaded': 'primary',
  'verified': 'success',
  'rejected': 'error',
};

const REPORT_STATUS_LABELS = {
  'new': 'Assigned Office',
  'assigned': 'Assigned Field',
  'in_progress': 'In Progress',
  'uploaded': 'Uploaded',
  'verified': 'Verified',
  'rejected': 'Rejected',
};

const REPORT_STATUS_ICONS = {
  'new': <AssignmentIcon />,
  'assigned': <PersonIcon />,
  'in_progress': <RotateRightIcon />,
  'uploaded': <UploadIcon />,
  'verified': <CheckIcon />,
  'rejected': <RejectIcon />,
};

const ApplicationDetails = () => {
  const { id } = useParams();
  // const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role, id: curr_user_id, permissions } = useSelector((state) => state.auth);
  
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: '',
    title: '',
  });
  const [actionNotes, setActionNotes] = useState('');

  const [selectedHeads, setSelectedHeads] = useState({});

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isPrintingReport, setIsPrintingReport] = useState(false);

  // Fetch application details
  const { data: applicationResponse, isLoading } = useQuery({
    queryKey: ['application', id],
    queryFn: () => {
      // Determine if id is a reference number (format: SK2025MN0001) or numeric ID
      if (typeof id === 'string' && /^[A-Z]{2}\d{4}[A-Z]{2}\d{4}$/.test(id)) {
        // It's a reference number (e.g., SK2025MN0001)
        // console.log('Fetching application by reference number:', id);
        return applicationService.getApplicationByRef('refno', id);
      } else {
        // It's a numeric ID
        // console.log('Fetching application by ID:', id);
        return applicationService.getApplicationByRef('id', id);
      }
    },
    staleTime: Infinity,
  });

  // Extract application from response
  const application = applicationResponse?.application;
  const appl_actions = applicationResponse?.actions || [];
  const head_of_office = applicationResponse?.head_of_office || {};

  // Fetch workflow history
  const { data: workflowHistory, isLoading: isLoadingWorkflow } = useQuery({
    queryKey: ['workflow-history', id],
    queryFn: () => applicationService.getWorkflowHistory(application.id),
    enabled: !!application?.id,
    staleTime: Infinity,
  });

  const workflow_hist = workflowHistory?.history || [];

  const workflow_notes_ordered = workflow_hist.length > 0
                                ?  [...workflow_hist].sort(
                                            (a, b) => new Date(b.created_at) - new Date(a.created_at)
                                    )
                                : [];

  //Fetch field inspection reports
  const { data: fieldInspectionReports, isLoading: isLoadingFieldInspection } = useQuery({
    queryKey: ['field-inspection-reports', id],
    queryFn: () => fieldInspectionService.getReportsByApplication(application.id),
    enabled: !!application?.id,
    staleTime: Infinity,
  });

  const field_inspection_reports = fieldInspectionReports || [];
  const allDamageReportsVerified = field_inspection_reports.length > 0 
                                      ? field_inspection_reports.every((report) => report.is_verified) 
                                      : false;

  // Workflow action mutation
  const workflowMutation = useMutation({
    mutationFn: ({ actionType, data }) => {
      switch (actionType) {
        case 'assign_to_sdm':
          return applicationService.assignApplicationToSDM(application.id, data);
        case 'damage_inspection':
          return applicationService.initiateDamageInspection(application.id, data);
        case 'forward_to_dpo':
          return applicationService.forwardApplicationToDPO(application.id, data);
        case 'forward_to_dc':
          return applicationService.forwardApplicationToDC(application.id, data);
        case 'forward_to_accounts':
          return applicationService.forwardApplicationToAccounts(application.id, data);
        case 'cheque_entry':
          return applicationService.chequeEntry(application.id, data);
        case 'disburse_cheque':
          return applicationService.disburseCheque(application.id, data);
        case 'backward_for_review':
          return applicationService.backwardApplicationForReview(application.id, data);
        case 'reject_application':
          return applicationService.rejectApplication(application.id, data);
        case 'complete':
          return applicationService.completeWorkflowStep(application.id, data);
        case 'reject':
          return applicationService.rejectApplication(application.id, data);
        case 'initiate_exgratia_calculation':
          return applicationService.initiateExgratiaCalculation(application.id, data);
        case 'update_exgratia_amount':
          return applicationService.updateExgratiaAmount(application.id, data);
        default:
          throw new Error('Invalid action type');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['application', id]);
      queryClient.invalidateQueries(['workflow-history', id]);
      toast.success('Action completed successfully');
      setActionDialog({ open: false, type: '', title: '' });
      setActionNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Action failed');
    },
  });

  const handleActionClick = (type, title) => {
    setActionDialog({ open: true, type, title });
  };

  const handleActionInitiateExgratiaCalc = () => {
    workflowMutation.mutate({
      actionType: 'initiate_exgratia_calculation',
      data: { },
    });
  };

  const handleToggleHead = (headCode, staffId) => {
    setSelectedHeads((prev) => ({ ...prev, [headCode]: staffId }));
  };

  const handleActionSubmit = () => {
    if (actionNotes.trim() === '') {
      toast.error('Note sheet cannot be empty! Please add notes.');
      return;
    }
    const data = { notes: actionNotes };
    
    if (actionDialog.type === 'damage_inspection') {
      const requiredHeads = DAMAGE_INSPECTION_SECTIONS
                                .filter(section => section.damageTypes.some(dt => application.damage_type.includes(dt)))
                                .flatMap(section => section.heads)
                                .map(head => head.code);
      const missingHeads = requiredHeads.filter(head => !selectedHeads[head]);
      if (missingHeads.length > 0) {
        toast.error('Please select heads of office for all applicable damage types.');
        return;
      }

      data.forward_to_heads = selectedHeads;
    }
    workflowMutation.mutate({
      actionType: actionDialog.type,
      data,
    });
  };

  const handleCloseDialog = () => {
    setActionDialog({ open: false, type: '', title: '' });
    setActionNotes('');
    setSelectedHeads({});
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setSelectedReport(null);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setReportDialogOpen(true);
  };

  const handlePrintReport = async (report) => {
    setIsPrintingReport(true);
    try {
      const blob = await applicationService.downloadApplFullReport(application.id);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `exgratia_report_${application.application_refno || application.id}_${application.applicant_name}.pdf`.replaceAll(' ', '_');
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
      console.error('Print error:', error);
    } finally {
      setIsPrintingReport(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'info';
      case 'under_review':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending_documents':
        return 'secondary';
      case 'completed':
        return 'success'
      default:
        return 'info';
    }
  };

  const getReportStatus = (report) => {
    if (report.is_rejected) return 'rejected';
    else if (report.is_verified) return 'verified';
    else if (report.is_report_uploaded) return 'uploaded';
    else if (report.is_assigned) return 'assigned';

    return 'new';
  };

  // Handle exgratia amount updates
  const handleExgratiaAmountUpdate = (category, newAmount) => {
    console.log(`Updating ${category}:`, newAmount);
    workflowMutation.mutate({
      actionType: 'update_exgratia_amount',
      data: {
        category: category,
        new_amount: newAmount
      },
    });
  };

  // Handle Cheque Details entry
  const handleChequeDetailsEntry = (updatedData) => {
    console.log('Cheque Details Entry:', updatedData.get('data'));
    workflowMutation.mutate({
      actionType: 'cheque_entry',
      data: updatedData,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSnakeCaseToTitle = (str) => {
    if (!str) return '';
    return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }

  const scrollToNote = (noteId) => {
    const element = document.getElementById(noteId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.style.transition = 'background-color 1s ease-in-out';
      element.style.backgroundColor = '#ffff99';

      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 2500);
    }
  };

  const canEditExgratiaAmount = permissions.includes('edit_exgratia_amount') && ((appl_actions.hasOwnProperty('calculate_exgratia_amount')) || (appl_actions.hasOwnProperty('update_exgratia_amount')));
  const canEditChequeDetails = permissions.includes('process_cheque') && (appl_actions.hasOwnProperty('cheque_entry'));

  const canEdit = permissions.includes('edit_exgratia') && application?.status === 'submitted';
  const canForward = permissions.includes('process_application');
  const canApprove = permissions.includes('approve_application');
  const canReject = permissions.includes('reject_application');

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!application) {
    return (
      <Alert severity="error">
        Application not found
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Application Details | Step {application.current_step}
            <Chip
              label={formatSnakeCaseToTitle(application.status)}
              color={getStatusColor(application.status)}
              sx={{ ml: 2 }}
            />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Reference: {application.application_refno || `APP-${application.id}`}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          { application.status === 'completed' && (
            <Button
              variant="outlined"
              startIcon={isPrintingReport ? <CircularProgress size={20} /> : <PrintIcon />}
              onClick={() => handlePrintReport(application)}
              disabled={isPrintingReport}
            >
              {isPrintingReport ? 'Downloading...' : 'Print Full Report'}
            </Button>
          )}
          {/* {canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/applications/${id}/edit`)}
            >
              Edit
            </Button>
          )} */}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          {/* Cheque Details */}
          {
            ((application.cheque_details !== null) || canEditChequeDetails) && (
                <Card sx={{ mb: 3 }}>
                  <CardHeader
                    sx={{backgroundColor: 'primary.dark', color: 'white'}}
                    title="Cheque Details"
                  />
                  <CardContent>
                    <ChequeDetails 
                      chequeData={application.cheque_details}
                      canEdit={canEditChequeDetails}
                      onUpdate={handleChequeDetailsEntry}
                      payeeName={application.applicant_name.toUpperCase()}
                      totalExgratiaAmount={application.final_exgratia_amount || 0 }
                    />
                  </CardContent>
                </Card>
            )
          }

          {/* Exgratia Calculation */}
          {
            !isLoadingFieldInspection && allDamageReportsVerified && (
                <Card sx={{ mb: 3 }}>
                  <CardHeader
                    sx={{backgroundColor: 'primary.dark', color: 'white'}}
                    title="Exgratia Calculation"
                  />
                  <CardContent>
                    {
                      !application.exgratia_norms_id && (
                        <Box sx={{ mt: 2, border: '1px solid', padding: 2, borderRadius: 1, borderColor: 'grey.300', pt: 2, backgroundColor:'white'}}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            All Field Inspection Reports are verified. You can now proceed to calculate the Exgratia Amount.
                          </Typography>
                          <Button
                            fullWidth
                            variant="outlined"
                            title="Proceed to Calculate Exgratia Amount"
                            onClick={() => handleActionInitiateExgratiaCalc()}
                          >
                            Proceed to Calculate Exgratia
                          </Button>
                        </Box>
                    )}
                    {
                      application.exgratia_amount && (
                        <Box sx={{ mt: 2 }}>
                          <ExgratiaAmountTable
                            exgratiaData={application.exgratia_amount}
                            canEdit={canEditExgratiaAmount}
                            onAmountUpdate={handleExgratiaAmountUpdate}
                          />
                        </Box>
                      )
                    }
                  </CardContent>
                </Card>
          )}

          {/* Field Inspection Report */}
          {
            !isLoadingFieldInspection && field_inspection_reports.length > 0 && (
              <Card sx={{ mb: 3}}>
                <CardHeader
                  sx={{ backgroundColor: 'success.dark', color: 'primary.contrastText' }}
                  title={`Field Inspection Reports (${field_inspection_reports.length} Nos.)`}
                />
                <CardContent>
                  {
                    isLoadingFieldInspection 
                      ? (<CircularProgress size={24} /> ) 
                      : field_inspection_reports.map((report, index) => {
                          const status = getReportStatus(report);
                          return (
                            <Box 
                              key={index} 
                              onClick={() => handleViewReport(report)}
                              sx={{ 
                                cursor: 'pointer', 
                                backgroundColor: 'grey.100',
                                '&:hover': { 
                                  backgroundColor: 'grey.200',
                                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                  transform: 'scale(1.01)',
                                  transition: 'transform 0.1s ease-in, background-color 0.1s ease-in, box-shadow 0.1s ease-in',
                                },
                                transition: 'background-color 0.1s ease-in, box-shadow 0.1s ease-in, transform 0.1s ease-in',
                                // borderRadius: 0,
                                mb: 1,
                                p: 2,
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                fontWeight="medium"
                                component="span"
                              >
                                Report # {report.id} - 
                                <Chip
                                  label={report.for_damage_type.map(dt => dt.charAt(0).toUpperCase() + dt.slice(1)).join(', ')}
                                  size="small"
                                  sx={{ ml: 1, backgroundColor: 'primary.main', color: 'white' }}
                                />
                                <Chip
                                  label={REPORT_STATUS_LABELS[status]}
                                  icon={REPORT_STATUS_ICONS[status]}
                                  color={REPORT_STATUS_COLORS[status]}
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
                              <Divider sx={{ my: 0.5 }} />

                              <Grid container spacing={4} 
                                sx={{ 
                                  mt: 2, 
                                  backgroundColor: 'rgba(255, 255, 255, 0.6)', 
                                  p: 2
                                }}
                              >
                                {
                                  report.is_report_uploaded ? (
                                    <Grid size={{ xs: 12, sm: 8 }} sx={{ backgroundColor: '#222222', color: 'white', p: 2, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'grey.400', mb: 0.5 }}>
                                        Report Uploaded on {format(new Date(report.uploaded_at), 'dd/MM/yyyy HH:mm')}
                                      </Typography>                                        
                                      <Typography variant="h6" sx={{ mt: 1 }}>
                                        <strong>Inspection Remarks: </strong>{report.brief_remarks?.toUpperCase() || 'N/A'}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mt: 4, borderTop: '1px solid', borderColor: 'grey.400', pt: 1 }}>
                                        <strong>Verif. Conducted with: </strong> {report.verif_conducted_with?.toUpperCase() || 'N/A'}
                                      </Typography>
                                    </Grid>
                                  ) : (
                                    <Grid size={{ xs: 12, sm: 8 }} sx={{ color: 'error.main'}}>
                                      <Typography variant="h6" sx={{ mt: 1 }}>
                                        Field Inspection Pending.
                                      </Typography>
                                    </Grid>
                                  )
                                }
                                <Grid size={{ xs: 12, sm: 4 }} sx={{ py: 1 }}>
                                  {
                                    report.verified_at && (
                                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                                        {format(new Date(report.verified_at), 'dd/MM/yyyy HH:mm')}
                                        <Chip
                                          label='Report Verified'
                                          icon={<CheckIcon />}
                                          color='success'
                                          size="small"
                                          sx={{ ml: 1 }}
                                          />
                                      </Typography>
                                    )
                                  }
                                  {
                                    report.uploaded_at && (
                                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                                        {format(new Date(report.uploaded_at), 'dd/MM/yyyy HH:mm')} 
                                        <Chip
                                          label='Report Uploaded'
                                          icon={<UploadIcon />}
                                          color='primary'
                                          size="small"
                                          sx={{ ml: 1 }}
                                          />
                                      </Typography>
                                    )
                                  }
                                  {
                                    report.assigned_at && (
                                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                                        {format(new Date(report.assigned_at), 'dd/MM/yyyy HH:mm')} 
                                        <Chip
                                          label='Assigned Field Insp.'
                                          icon={<AssignmentIcon />}
                                          color='warning'
                                          size="small"
                                          sx={{ ml: 1 }}
                                        />
                                      </Typography>
                                    )
                                  }
                                  <Typography variant="caption" 
                                    sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}
                                  >
                                    {format(new Date(report.requested_at), 'dd/MM/yyyy HH:mm')} 
                                    <Chip
                                      label='Requested by SDM'
                                      icon={<PersonIcon />}
                                      color='info'
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="body2" sx={{ color: 'grey.800', fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'grey.400', mb: 0.5 }}>
                                    Field Head of Office
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'grey.800' }}>
                                    {report.field_head_official.role_full_name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    ({report.field_head_official.full_name}, {report.field_head_official.designation})
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="body2" 
                                    sx={{ 
                                      color: 'grey.800', 
                                      fontWeight: 'bold', 
                                      borderBottom: '1px solid', 
                                      borderColor: 'grey.400', 
                                      mb: 0.5 
                                    }}
                                  >
                                    Assigned Field Inspection Official
                                  </Typography>
                                  {
                                    report.field_assigned_user ? (
                                      <>
                                      <Typography variant="body2" sx={{ color: 'grey.800' }}>
                                        {report.field_assigned_user.role.full_name}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        ({report.field_assigned_user.full_name}, {report.field_assigned_user.designation})
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                        Assigned on {report.assigned_at && format(new Date(report.assigned_at), 'dd/MM/yyyy HH:mm')}
                                      </Typography>
                                      </>
                                    ) : (
                                      <Typography 
                                        variant="h6" 
                                        sx={{ 
                                          color: 'error.main',
                                          // backgroundColor: '#222222',
                                          p: 2, 
                                          }}>
                                        Field Inspection Official Not Assigned yet.
                                      </Typography>
                                    )
                                  }
                                </Grid>
                              </Grid>
                            </Box>
                          )
                      })
                  }
                </CardContent>
              </Card>
            )
          }
          <Grid container spacing={2}>
            {/* Application Information */}
            <Grid size={{xs: 12, lg:6 }}>
              {/* Basic Details */}
              <Card sx={{ mb: 2 }}>
                <CardHeader
                    sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}
                    title="Basic Details"
                  />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Applicant Name
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.applicant_name}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Son/Daughter/Wife of
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.sodowo}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.ph_number}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Voter ID
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.voter_id.toUpperCase()}
                      </Typography>
                    </Grid>

                    <Divider sx={{ width: '100%'}} />

                    <Grid size={{ xs: 12, sm: 12 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Applicant Remarks
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.remarks}
                      </Typography>
                    </Grid>

                  </Grid>
                </CardContent>
              </Card>

              {/* Supporting Images */}
              {application.supporting_images && application.supporting_images.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardHeader
                    sx={{ backgroundColor: 'info.main', color: 'white' }}
                    title="Submitted Images"
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      {application.supporting_images.map((imagePath, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                          <ImageViewer
                            imagePath={imagePath}
                            alt={`Supporting document ${index + 1}`}
                            title={`Document ${index + 1}`}
                            description="Click to view full size"
                            thumbnailWidth="100%"
                            thumbnailHeight="200px"
                            borderRadius={1}
                            showHoverEffects={true}
                            showDescriptionOverlay={true}
                            downloadFilename={`application_${application.application_refno}_doc_${index + 1}.jpg`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Location Details */}
              <Card sx={{ mb: 2 }}>
                <CardHeader
                    sx={{ backgroundColor: 'info.main', color: 'primary.contrastText' }}
                    title="Location Details"
                  />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Village
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.village}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Ward
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.ward.name}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        GPU
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.ward.gpu.name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Block
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.ward.gpu.block.name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Sub-Division
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.ward.gpu.block.subdivision.name}
                      </Typography>
                    </Grid>

                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        District
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.ward.gpu.block.subdivision.district.name} | {application.district}
                      </Typography>
                    </Grid>
                    
                    {application.land_khatian_number && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Land Khatian Number
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {application.land_khatian_number}
                        </Typography>
                      </Grid>
                    )}
                    
                    {application.land_plot_nos && application.land_plot_nos.length > 0 && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Land Plot Numbers
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {application.land_plot_nos.join(', ')}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Damage Information */}
              <Card sx={{ mb: 2 }}>
                <CardHeader
                  sx={{ backgroundColor: 'info.main', color: 'white' }}
                  title="Damage Information"
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Damage Types
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {application.damage_type?.map((type, index) => (
                          <Chip
                            key={index}
                            label={type}
                            variant="outlined"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))}
                      </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Natural Calamity Date & Time
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.actual_nc_datetime && 
                          formatDate(application.actual_nc_datetime)}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Application Date
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {application.created_at && 
                          formatDate(application.created_at)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Notes */}
            <Grid size={{xs: 12, lg:6}}>
              <Card sx={{ mb: 2}}>
                <CardHeader
                  sx={{backgroundColor: 'primary.main', color: 'white'}}
                  title="Notes"
                />
                <CardContent sx={{ backgroundColor: '#e0fdd1', p: 0}}>
                {
                  isLoadingWorkflow
                  ? (<CircularProgress />)
                  : workflow_notes_ordered && workflow_notes_ordered.length > 0 ? (
                        workflow_notes_ordered.map((step, index) => (
                          step.notes && (
                            <Grid container key={index}
                              id={`note_${workflow_notes_ordered.length - index}`}
                              sx={{ 
                                py: 1, 
                                px: 2,
                                borderBottom: index < workflow_notes_ordered.length - 1 ? '2px solid' : 'none', 
                                borderColor: 'grey.400',
                                mb: 1
                            }}>
                              <Grid size={{ xs: 12 }} >
                                <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ mb: 0.25, lineHeight: 1.2 }}>
                                  Note #{workflow_notes_ordered.length - index} | {step.purpose}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', lineHeight: 1.1 }}>
                                  {formatSnakeCaseToTitle(step.action)} | Step {step.from_step} {'->'} Step {step.to_step}
                                </Typography>
                                <Typography variant="body1" sx={{ 
                                  mb: 2, 
                                  // px: 1, 
                                  lineHeight: 1.3,
                                  whiteSpace: 'pre-line'
                                }}>
                                  {step.notes.content || ''}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  alignItems: 'start',
                                  // p: 1,
                                  borderRadius: 1
                                }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                      <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                      {format(new Date(step.notes.created_at), 'dd MMMM, yyyy HH:mm')}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <Typography variant="caption" fontWeight="bold" color="text.primary" sx={{ lineHeight: 1.2 }}>
                                      {step.notes.created_by}
                                    </Typography>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                      {step.notes.designation}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          )
                        ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                        No Notes available
                      </Typography>
                  )
                }
                </CardContent>
              </Card>
            </Grid>

            
                      
          </Grid>
        </Grid>

        {/* Actions and Workflow */}
        <Grid size={{ xs: 12, lg: 4 }}>
          {/* Actions */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              sx={{ backgroundColor: 'primary.dark', color: 'primary.contrastText' }}
              title="Actions"
            />
            <CardContent>              
              <Box display="flex" flexDirection="column" gap={1}>
                {application.status === 'completed'
                  ? (
                    <Typography variant="body2" sx={{ color: 'white', mt : 1, backgroundColor: 'success.main', p: 2, borderRadius: 1, fontWeight: 'bold' }}>
                      Note: This application has been completed. You can only view the details and workflow history.
                    </Typography>
                  ) : application.status === 'rejected'
                      ? (
                        <Typography variant="body2" sx={{ color: 'white', mt : 1, backgroundColor: 'error.main', p: 2, borderRadius: 1, fontWeight: 'bold' }}>
                          Note: This application has been rejected. You can only view the details and workflow history.
                        </Typography>
                      ) : application.status !== 'submitted' && application.assigned_to !== curr_user_id
                          ? (
                              <Typography variant="body2" sx={{ color: 'text.secondary', mt : 1 }}>
                                Note: This application is currently not with you.
                                You can only view the details and workflow history.
                              </Typography>
                            ) : appl_actions.length === 0 && (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mt : 1 }}>
                                    Note: There are no available actions for this application. Kindly wait for further updates.
                                  </Typography>
                                )
                }

                {
                  (application.status !== 'completed' && application.status !== 'rejected') && appl_actions && Object.keys(appl_actions).map((key) => {
                    const action = appl_actions[key];

                    const show_action = action.hasOwnProperty('display') ? action.show : true;

                    if (!show_action) {
                      return null;
                    }
                    if (action.condition) {
                      if (application[action.condition.field] !== action.condition.value) return null;

                      return (
                        <Box key={key} mb={1} sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: "primary.main", fontWeight: "bold" }}>
                            {action.condition.message}
                          </Typography>
                          <Button
                            fullWidth
                            variant="outlined"
                            title={action.description}
                            onClick={() => handleActionClick(key, action.description)}
                          >
                            {action.title}
                          </Button>
                        </Box>
                      );
                    }

                    const btn_label = key === 'assign_to_sdm'
                      ? `Assign to SDM (${application.ward.gpu.block.subdivision.name.toUpperCase()})`
                      : action.title;

                    const start_icon = action.icon || (key === 'assign_to_sdm' ? <PostAddIcon /> : '');

                    return (
                      <Button
                        key={key}
                        fullWidth
                        variant="outlined"
                        title={action.description}
                        onClick={() => handleActionClick(
                            key, 
                            btn_label
                        )}
                        startIcon={start_icon}
                        sx={{ mb: 1 }}
                      >
                        {btn_label}
                      </Button>
                    );
                  })
                }

                {/* {canForward && (
                  <>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ForwardIcon />}
                    onClick={() => handleActionClick('forward', 'Forward Application')}
                  >
                    Forward
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<BackwardIcon />}
                    onClick={() => handleActionClick('backward', 'Send Back')}
                  >
                    Send Back
                  </Button>
                  </>
                )}
                
                {canApprove && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => handleActionClick('complete', 'Approve Application')}
                  >
                    Approve
                  </Button>
                )}
                
                {canReject && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => handleActionClick('reject', 'Reject Application')}
                  >
                    Reject
                  </Button>
                )} */}

                {role?.name === 'citizen' && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Note: As a citizen, you can only view your application details and status.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Workflow History */}
          <Card>
            <CardHeader
              sx={{ backgroundColor: 'info.dark', color: 'primary.contrastText' }}
              title="Workflow History"
            />
            <CardContent>
              {
                isLoadingWorkflow
                  ? (<CircularProgress />)
                  : workflow_hist && workflow_hist.length > 0 ? (
                      <Box sx={{ position: 'relative', pl: 3 }}>
                        {workflow_hist.map((step, index) => (
                          <Box key={index} sx={{ position: 'relative', pb: 3 }}>
                            {/* Timeline Line */}
                            {index < workflow_hist.length - 1 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: '-12px',
                                  top: '32px',
                                  bottom: '-12px',
                                  width: '2px',
                                  backgroundColor: 'grey.300',
                                  zIndex: 1,
                                }}
                              />
                            )}
                            
                            {/* Timeline Dot */}
                            <Avatar
                              sx={{
                                position: 'absolute',
                                left: '-20px',
                                top: '8px',
                                width: 16,
                                height: 16,
                                bgcolor: step.action === 'approved' ? 'success.main' : 
                                        step.action === 'rejected' ? 'error.main' : 'primary.main',
                                zIndex: 2,
                              }}
                            >
                              <Box />
                            </Avatar>

                            {/* Content */}
                            <Box 
                              sx={{ ml: 2, }}
                            >
                              <Box
                                sx={{
                                      cursor: 'pointer', 
                                      '&:hover': { 
                                        transform: 'scale(1.01)',
                                        color: 'info.dark',
                                        transition: 'all 0.2s ease-in-out', 
                                      }
                                }}
                                onClick={() => scrollToNote(`note_${index + 1}`)}
                                title="Click to view associated note"
                              >
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                  <Typography 
                                    variant="subtitle2" 
                                    fontWeight="medium"
                                  >
                                      #{index + 1} {formatSnakeCaseToTitle(step.action)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {step.created_at && format(new Date(step.created_at), 'dd/MM HH:mm')}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ mb: 1.5, backgroundColor: 'grey.100', p: 0.5, borderRadius: 1 }}>
                                  Status: {formatSnakeCaseToTitle(step.to_status)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, display: 'block' }}>
                                  by {step.staff.full_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, px: 0.5, display: 'block' }}>
                                  ({ step.staff.designation || 'System' })
                                </Typography>
                              </Box>
                              
                              
                              {step.action === 'damage_inspection' && (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px dashed',
                                    borderColor: 'grey.300',
                                    maxWidth: '300px',
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                    Field Inspection Reports:
                                  </Typography>

                                  <Divider sx={{ my: 1 }} />

                                  { 
                                    isLoadingFieldInspection 
                                      ? (<CircularProgress size={24} /> ) 
                                      : field_inspection_reports.map((report, index) => {
                                          const status = getReportStatus(report);
                                          return (<Box 
                                            key={index} 
                                            onClick={() => handleViewReport(report)}
                                            sx={{ 
                                              cursor: 'pointer', 
                                              '&:hover': { backgroundColor: 'grey.100' },
                                              borderRadius: 1,
                                              p: 1,
                                            }}
                                          >
                                            <Typography 
                                              variant="body2" 
                                              fontWeight="medium"
                                              component="span"
                                            >
                                              Report # {report.id} - 
                                              <Chip
                                                label={REPORT_STATUS_LABELS[status]}
                                                icon={REPORT_STATUS_ICONS[status]}
                                                color={REPORT_STATUS_COLORS[status]}
                                                size="small"
                                                sx={{ ml: 1 }}
                                              />
                                            </Typography>
                                            <Divider sx={{ my: 0.5 }} />
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                              {report.field_head_official.full_name}, {report.field_head_official.designation}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'grey.800' }}>
                                              ({report.field_head_official.role_full_name})
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                              {report.requested_at && format(new Date(report.requested_at), 'dd/MM HH:mm')}
                                            </Typography>
                                          </Box>)
                                      })
                                    }
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                        No workflow history available
                      </Typography>
                    )
              }
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Report Details Dialog */}
      <ReportDetailsDialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        selectedReport={selectedReport}
        reportActions={() => {}}
        formatDate={formatDate}
        getReportStatus={getReportStatus}
      />

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{actionDialog.title}</DialogTitle>
        <Divider />
        <DialogContent>
          {/* Damage Inspection: damage type sections with heads */}
          {actionDialog.type === 'damage_inspection' && application?.damage_type && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                Assign appropriate Heads of Office to initiate damage inspection:
              </Typography>
              {DAMAGE_INSPECTION_SECTIONS.map((section) => {
                const isApplicable = section.damageTypes.some(
                  (dt) => application.damage_type.includes(dt)
                );
                const matchedTypes = section.damageTypes.filter(
                  (dt) => application.damage_type.includes(dt)
                );
                return (
                  <Box
                    key={section.section}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      mb: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isApplicable ? 'primary.main' : 'grey.300',
                      backgroundColor: isApplicable ? 'primary.50' : 'grey.50',
                      opacity: isApplicable ? 1 : 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        pr: 2,
                        mr: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={isApplicable ? 'primary.main' : 'text.disabled'}
                      >
                        {section.section}
                      </Typography>
                      {isApplicable && (
                        <Typography variant="caption" color="text.secondary">
                          {matchedTypes.map((dt) => dt.charAt(0).toUpperCase() + dt.slice(1)).join(', ')}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {section.heads.map((head) => (
                        <Box key={head.code}>
                          <Typography variant="subtitle2"
                            display="block" 
                            sx={{ 
                              color: 'text.primary',
                              fontWeight: 'medium',
                              ml: 0.5, 
                              mb: 0.25 
                              }}
                          >
                            {head.label}
                          </Typography>
                          <Divider sx={{ my: 0.5 }} />

                          {isApplicable && head_of_office && (
                            head_of_office[head.code]?.length > 0
                              ? (
                                  <RadioGroup
                                    value={selectedHeads[head.code] || ''}
                                    onChange={(e) => handleToggleHead(head.code, Number(e.target.value))}
                                  >
                                    {head_of_office[head.code].map((staff) => (
                                      <FormControlLabel
                                        key={staff.id}
                                        value={staff.id}
                                        control={<Radio size="small" color="success" />}
                                        label={
                                          <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 'medium' }}>
                                            {staff.full_name}, {staff.designation}
                                          </Typography>
                                        }
                                        sx={{ 
                                          mr: 0, 
                                          justifyContent: 'flex-start', 
                                          backgroundColor: 'grey.100', 
                                          borderRadius: 1, 
                                          mb: 0.25, px: 1,
                                        }}
                                      />
                                    ))}
                                  </RadioGroup>
                                )
                              : (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{ mt: 0.25, color: 'warning.dark', textAlign: 'right' }}
                                  >
                                    No staff assigned
                                  </Typography>
                                )
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
              <Divider sx={{ mt: 1 }} />
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comments/Notes"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleActionSubmit}
            variant="contained"
            disabled={workflowMutation.isPending}
          >
            {workflowMutation.isPending ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      <LoadingOverlay
        open={isLoading || isLoadingWorkflow || isLoadingFieldInspection || workflowMutation.isPending}
        message={
          isLoading 
            ? 'Loading application details...' 
            : isLoadingWorkflow 
              ? 'Loading workflow history...'
              : isLoadingFieldInspection
                ? 'Loading field inspection reports...'
                : workflowMutation.isPending
                  ? 'Processing workflow action...'
                  : 'Loading...'
        }
      />
    </Box>
  );
};

export default ApplicationDetails;
